import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

// Fenêtre de publication (popup) — cadre futuriste, responsive, autoplay des visuels.
export default function PromoPopup() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const location = useLocation();

  useEffect(() => {
    // Ne pas afficher sur /connexion ou les pages d'administration pour ne pas bloquer la saisie
    if (location.pathname.startsWith("/connexion") || location.pathname.startsWith("/admin")) {
      setOpen(false);
      return;
    }
    api.get("/promo-popup").then(({ data }) => {
      if (!data?.enabled || !data.slides?.length) return;
      const seenKey = `promo_seen_${data.version || "v"}`;
      if (data.once_per_session && sessionStorage.getItem(seenKey)) return;
      setCfg(data);
      const t = setTimeout(() => {
        setOpen(true);
        if (data.once_per_session) sessionStorage.setItem(seenKey, "1");
      }, Math.max(0, (data.delay_seconds ?? 2) * 1000));
      return () => clearTimeout(t);
    }).catch(() => {});
  }, [location.pathname]);

  const slides = cfg?.slides || [];

  useEffect(() => {
    if (!open || slides.length < 2) return;
    const dur = (slides[idx]?.duration_seconds || 5) * 1000;
    const t = setTimeout(() => setIdx((i) => (i + 1) % slides.length), dur);
    return () => clearTimeout(t);
  }, [open, idx, slides]);

  if (!cfg || !slides.length) return null;
  const slide = slides[idx] || slides[0];
  const link = (slide.link_url || "").trim();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="promo-popup-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9997] flex items-center justify-center p-4 sm:p-6"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-[#03060f]/80 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.85, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[420px] sm:max-w-[480px] lg:max-w-[520px]"
            data-testid="promo-popup"
          >
            {/* halo lumineux animé */}
            <div className="pointer-events-none absolute -inset-6 -z-10 animate-pulse rounded-[32px] bg-gradient-to-tr from-cyan-500/40 via-blue-600/30 to-fuchsia-500/40 blur-2xl" />

            {/* cadre néon dégradé */}
            <div className="rounded-[26px] bg-gradient-to-br from-cyan-400 via-blue-600 to-fuchsia-500 p-[2px] shadow-[0_0_60px_-10px_rgba(37,99,235,0.7)]">
              <div className="relative overflow-hidden rounded-[24px] bg-[#070b18]">
                {/* bouton fermer 3D */}
                <button
                  type="button" data-testid="promo-popup-close" onClick={() => setOpen(false)}
                  aria-label="Fermer"
                  className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/30 backdrop-blur transition-all duration-200 hover:scale-110 hover:bg-rose-500/90 active:scale-95 shadow-[0_6px_18px_-4px_rgba(244,63,94,0.6)]"
                >
                  <X className="h-5 w-5" strokeWidth={2.6} />
                </button>

                {/* média (image / gif / vidéo) */}
                <a
                  href={link || undefined} target={link ? "_blank" : undefined} rel="noopener noreferrer"
                  data-testid="promo-popup-media-link"
                  className={`block ${link ? "cursor-pointer" : "cursor-default"}`}
                  onClick={(e) => { if (!link) e.preventDefault(); }}
                >
                  <div className="relative flex max-h-[70vh] items-center justify-center bg-[#070b18]">
                    <AnimatePresence mode="wait">
                      <motion.div key={idx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="w-full">
                        {slide.media_kind === "video" ? (
                          <video
                            src={fileUrl(slide.image_url)} autoPlay muted loop playsInline
                            className="max-h-[70vh] w-full object-contain"
                          />
                        ) : (
                          <img
                            src={fileUrl(slide.image_url)} alt="Promotion" loading="eager" decoding="async"
                            className="max-h-[70vh] w-full object-contain"
                          />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </a>

                {/* bas : puces + CTA */}
                <div className="flex items-center justify-between gap-3 bg-gradient-to-t from-[#070b18] via-[#070b18]/95 to-transparent px-4 pb-4 pt-3">
                  <div className="flex gap-1.5" data-testid="promo-popup-dots">
                    {slides.length > 1 && slides.map((_, i) => (
                      <button key={i} type="button" onClick={() => setIdx(i)} aria-label={`Visuel ${i + 1}`}
                        className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-cyan-400" : "w-2 bg-white/30 hover:bg-white/60"}`} />
                    ))}
                  </div>
                  {link && (
                    <a
                      href={link} target="_blank" rel="noopener noreferrer" data-testid="promo-popup-cta"
                      className="group relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-5 py-2.5 text-sm font-extrabold text-white shadow-[0_10px_24px_-6px_rgba(37,99,235,0.8)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_30px_-6px_rgba(37,99,235,0.9)] active:translate-y-0 active:scale-95"
                    >
                      Découvrir <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
